export const TAX_RATES_2025_26 = {
  PERSONAL_ALLOWANCE: 12570,
  PERSONAL_ALLOWANCE_THRESHOLD: 100000,
  BASIC_RATE: 0.20,
  HIGHER_RATE: 0.40,
  ADDITIONAL_RATE: 0.45,
  BASIC_RATE_THRESHOLD: 37700,
  HIGHER_RATE_THRESHOLD: 125140,
};

// Function to parse tax code and get personal allowance
function getPersonalAllowanceFromTaxCode(taxCode, grossAnnualSalary) {
  if (!taxCode) return TAX_RATES_2025_26.PERSONAL_ALLOWANCE;
  
  const code = taxCode.toUpperCase().trim();
  
  // Handle different tax code types
  if (code === '0T' || code === 'NT') {
    return 0; // No personal allowance
  }
  
  if (code.endsWith('L')) {
    // Standard L code - extract the number and multiply by 10
    const number = parseInt(code.replace('L', ''));
    if (!isNaN(number)) {
      const allowance = number * 10;
      // Still apply tapering for high earners even with L codes
      if (grossAnnualSalary > TAX_RATES_2025_26.PERSONAL_ALLOWANCE_THRESHOLD) {
        const reduction = Math.floor((grossAnnualSalary - TAX_RATES_2025_26.PERSONAL_ALLOWANCE_THRESHOLD) / 2);
        return Math.max(0, allowance - reduction);
      }
      return allowance;
    }
  }
  
  if (code.startsWith('K')) {
    // K code means negative allowance (owe tax on benefits)
    return 0;
  }
  
  if (code.endsWith('T')) {
    // T codes - extract number and multiply by 10
    const number = parseInt(code.replace('T', ''));
    if (!isNaN(number)) {
      return number * 10;
    }
  }
  
  // Fallback to standard allowance
  return TAX_RATES_2025_26.PERSONAL_ALLOWANCE;
}

export function calculatePersonalAllowance(grossAnnualSalary, taxCode = '1257L') {
  return getPersonalAllowanceFromTaxCode(taxCode, grossAnnualSalary);
}

export function calculateIncomeTax(grossAnnualSalary, pensionContribution = 0, taxCode = '1257L') {
  const taxableIncome = grossAnnualSalary - pensionContribution;
  const personalAllowance = calculatePersonalAllowance(taxableIncome, taxCode);
  const taxablePay = Math.max(0, taxableIncome - personalAllowance);
  
  let basicRate = 0;
  let higherRate = 0;
  let additionalRate = 0;
  
  if (taxablePay > 0) {
    // Basic rate: 20% on first £37,700
    basicRate = Math.min(taxablePay, TAX_RATES_2025_26.BASIC_RATE_THRESHOLD) * TAX_RATES_2025_26.BASIC_RATE;
    
    // Higher rate: 40% on £37,701 - £125,140
    if (taxablePay > TAX_RATES_2025_26.BASIC_RATE_THRESHOLD) {
      const higherRatePortion = Math.min(
        taxablePay - TAX_RATES_2025_26.BASIC_RATE_THRESHOLD,
        TAX_RATES_2025_26.HIGHER_RATE_THRESHOLD - TAX_RATES_2025_26.BASIC_RATE_THRESHOLD
      );
      higherRate = higherRatePortion * TAX_RATES_2025_26.HIGHER_RATE;
    }
    
    // Additional rate: 45% on £125,141+
    if (taxablePay > TAX_RATES_2025_26.HIGHER_RATE_THRESHOLD) {
      additionalRate = (taxablePay - TAX_RATES_2025_26.HIGHER_RATE_THRESHOLD) * TAX_RATES_2025_26.ADDITIONAL_RATE;
    }
  }
  
  const totalTax = basicRate + higherRate + additionalRate;
  
  return {
    personalAllowance,
    taxablePay,
    basicRate,
    higherRate,
    additionalRate,
    totalTax,
  };
}

export function calculateMonthlyIncomeTax(grossAnnualSalary, pensionContribution = 0, taxCode = '1257L') {
  const annualTax = calculateIncomeTax(grossAnnualSalary, pensionContribution, taxCode);
  return annualTax.totalTax / 12;
}